import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, GraduationCap, Users, Lock } from 'lucide-react';
import { api } from '@/lib/api';
import { formatEventDateRange } from '@/hooks/useActiveEvent';
import { cn } from '@/lib/utils';
import type { EventPublicInfo } from '@/types';

const CLOSED_MESSAGE = 'Registration is now closed';

function isDeadlinePassed(deadline: string | null | undefined, now: number): boolean {
  if (!deadline) return false;
  const t = new Date(deadline).getTime();
  return !isNaN(t) && now > t;
}

export default function RegisterLandingPage() {
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventPublicInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ success: boolean; data: EventPublicInfo }>('/events/active/public')
      .then((res) => setEvent(res.data))
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center space-y-3">
          <h1 className="text-2xl font-bold">Registration Not Available</h1>
          <p className="text-muted-foreground">There is no active event accepting registrations at this time.</p>
        </div>
      </div>
    );
  }

  const eventParam = `?event=${event.id}`;
  const now = Date.now();
  const exhibitorClosed = isDeadlinePassed(event.exhibitorRegistrationDeadline, now);
  const npsProjectClosed = isDeadlinePassed(event.npsProjectRegistrationDeadline, now);
  const attendeeClosed = isDeadlinePassed(event.attendeeRegistrationDeadline, now);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-navy text-white py-10 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-6">
          <img src="/nps-logo.png" alt="NPS Foundation" className="h-16 w-auto shrink-0" />
          <div className="space-y-1">
            <h1 className="text-3xl md:text-4xl font-bold">Converge @ NPS</h1>
            <p className="text-lg text-blue-100">
              {formatEventDateRange(event.dateStart, event.dateEnd)}
            </p>
            <p className="text-sm text-blue-200">
              {event.welcomeMessage || 'Mission-Driven Innovation — connecting tech & innovation with operational needs'}
            </p>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-sm text-muted-foreground space-y-3 mb-8">
          <p className="font-medium text-foreground text-center">Converge @ NPS: Mission-Driven Innovation provides a platform to...</p>
          <ul className="list-disc space-y-1 max-w-xl mx-auto pl-6">
            <li>Connect tech & innovation with operational needs.</li>
            <li>Discover and learn how to support research, education & innovation at NPS.</li>
            <li>Participate in breakout groups, workshops, and panels.</li>
            <li>Explore partnership opportunities for testing, prototyping & deploying solutions.</li>
            <li>Network with warfighters, defense-focused faculty, government, and industry.</li>
          </ul>
        </div>

        <h2 className="text-xl font-semibold mb-6 text-center">Select your registration type</h2>

        {/* Cards */}
        <div className="grid gap-5 md:grid-cols-3 items-stretch">
          <RegistrationCard
            icon={<Building2 className="h-5 w-5 text-primary" />}
            title="Exhibitor Application"
            description="For organizations interested in showcasing cutting-edge tech, participating in challenge breakout sessions, or leading technology workshops."
            buttonLabel="Apply as Exhibitor"
            onClick={() => navigate(`/register/exhibitor${eventParam}`)}
            closed={exhibitorClosed}
          />

          <RegistrationCard
            icon={<GraduationCap className="h-5 w-5 text-primary" />}
            title="NPS Project Submission"
            description="For current NPS students and faculty interested in pitching work to industry partners and government/military sponsors."
            buttonLabel="Submit a Project"
            onClick={() => navigate(`/register/nps-project${eventParam}`)}
            closed={npsProjectClosed}
          />

          <RegistrationCard
            icon={<Users className="h-5 w-5 text-primary" />}
            title="General Registration"
            description="For all attendees — NPS students, faculty, alumni, military, government, industry, and other participants."
            buttonLabel="Register to Attend"
            onClick={() => navigate(`/register/attendee${eventParam}`)}
            closed={attendeeClosed}
          />
        </div>

        <p className="text-xs text-muted-foreground text-center mt-8">
          Please send any questions to{' '}
          <a href="mailto:converge@npsfoundation.org" className="text-primary underline">
            converge@npsfoundation.org
          </a>
        </p>
      </div>
    </div>
  );
}

type RegistrationCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  buttonLabel: string;
  onClick: () => void;
  closed: boolean;
};

function RegistrationCard({ icon, title, description, buttonLabel, onClick, closed }: RegistrationCardProps) {
  return (
    <Card
      data-testid={`registration-card-${closed ? 'closed' : 'open'}`}
      aria-disabled={closed || undefined}
      className={cn(
        'flex flex-col transition-colors',
        closed ? 'opacity-60 bg-muted/40 border-dashed' : 'hover:border-primary/50'
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2 mb-2">
          {icon}
          <CardTitle className="text-base whitespace-nowrap">{title}</CardTitle>
        </div>
        <CardDescription className="text-xs leading-relaxed">{description}</CardDescription>
      </CardHeader>
      <CardContent className="mt-auto pt-0">
        {closed ? (
          <div
            role="status"
            className="w-full flex items-center justify-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm font-medium text-muted-foreground"
          >
            <Lock className="h-4 w-4" />
            {CLOSED_MESSAGE}
          </div>
        ) : (
          <Button className="w-full" onClick={onClick}>
            {buttonLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
